import type { FormattedInvoicePayload } from "../../formattedInvoice/formattedInvoiceData";
import { asArray, asRecord, type JsonRecord } from "./reportDesignerModel";

export type DesignerSample = {
  id: string;
  name: string;
  description: string;
  payload: FormattedInvoicePayload;
};

const formattedInvoicePayload: FormattedInvoicePayload = {
  definitionId: "designer-formatted-invoice-header",
  metadata: {
    reportName: "Formatted Invoice Header Designer",
  },
  assets: {},
  datasets: {
    invoiceHeader: {
      invoiceDate: "2026-10-02",
      clientName: "Admiral Permian Resources LLC",
      clientAddressLine1: "400 W. Illinois STE 970",
      clientAddressLine2: "Midland TX 79701",
      attentionName: "Travis Hutt",
      brokerName: "Bob Sample & Associates, Inc.",
      brokerAddressLine1: "4925 Greenville Ave. STE 1106",
      brokerAddressLine2: "Dallas TX 75206",
      invoiceNumber: "64",
      periodCovered: "09/01/2026 - 09/30/2026",
      dueTerms: "Net 30 from invoice date",
    },
    brokerSummary: {
      rows: [
        { brokerName: "Bob Sample & Associates, Inc.", days: 12.5, professionalServices: 3125, total: 3412.50 },
      ],
    },
  },
  layout: {
    styles: {
      invoiceHeaderFields: {
        fontSize: 9,
        pdfFontSize: 7,
        columns: 3,
        columnTemplate: ["1.18fr", "0.95fr", "1.22fr"],
        gapX: 10,
        gapY: 0,
        fieldGapY: 2,
        fieldGapX: 5,
        lineHeight: 1.15,
      },
      invoiceHeaderStackedField: {
        fieldLayout: "stacked",
        fieldGapY: 2,
      },
      invoiceHeaderStackedGroupField: {
        fieldLayout: "stacked",
        fieldGapY: 2,
        marginTop: 6,
        pdfMarginTop: 5,
      },
      invoiceHeaderInlineGroupField: {
        marginTop: 6,
        pdfMarginTop: 5,
        labelMinWidth: 95,
        pdfLabelMinWidth: 60,
      },
      invoiceHeaderTermsField: {
        color: "#ff0000",
        marginTop: 24,
        pdfMarginTop: 18,
      },
      invoiceSummaryTable: {
        fontSize: 10,
        pdfFontSize: 8,
        headerBackgroundColor: "#f3f4f6",
        headerBold: true,
      },
    },
    sections: [
      {
        id: "invoiceHeader",
        type: "keyValueGrid",
        dataset: "invoiceHeader",
        outputs: ["preview", "pdf", "excel"],
        style: "invoiceHeaderFields",
        fields: [
          { label: "Date:", key: "invoiceDate", format: "date", column: 1 },
          { label: "Attn:", key: "attentionName", style: "invoiceHeaderStackedField", column: 2 },
          { label: "BROKER", key: "brokerName", style: "invoiceHeaderStackedField", column: 3 },
          { label: "Bill To:", key: "clientName", style: "invoiceHeaderStackedGroupField", column: 1 },
          { label: "", key: "clientAddressLine1", column: 1 },
          { label: "", key: "clientAddressLine2", column: 1 },
          { label: "", key: "brokerAddressLine1", column: 3 },
          { label: "", key: "brokerAddressLine2", column: 3 },
          { label: "Invoice Number:", key: "invoiceNumber", style: "invoiceHeaderInlineGroupField", column: 3 },
          { label: "Period Covered:", key: "periodCovered", style: "invoiceHeaderStackedGroupField", column: 1 },
          { label: "", key: "dueTerms", style: "invoiceHeaderTermsField", column: 2 },
        ],
      },
      {
        id: "brokerSummaryTable",
        type: "table",
        dataset: "brokerSummary",
        outputs: ["preview", "pdf", "excel"],
        style: "invoiceSummaryTable",
        columns: [
          { key: "brokerName", label: "Broker", width: 24, wrap: true },
          { key: "days", label: "# Days", format: "number", width: 10, horizontalAlign: "right" },
          { key: "professionalServices", label: "Services", format: "money", width: 14, horizontalAlign: "right" },
          { key: "total", label: "Total", format: "money", width: 14, horizontalAlign: "right" },
        ],
      },
    ],
  },
};

const landmanStatusPayload: FormattedInvoicePayload = {
  definitionId: "designer-landman-status",
  metadata: {
    reportName: "Landman Status Designer",
  },
  assets: {},
  datasets: {
    statusSummaryBadges: [
      { label: "Invoiced", value: 2, status: "invoiced" },
      { label: "Pending", value: 1, status: "pending" },
    ],
    landmanStatusDetails: [
      {
        workDate: "2026-10-01",
        landman: "Taylor Smith",
        client: "Admiral Permian Resources LLC",
        prospect: "Midland Prospect",
        legal: "Sec 12 Blk A T1S R2E Abs 123",
        jobType: "Title",
        days: 1,
        totalExpenses: 85,
      },
      {
        workDate: "2026-10-02",
        landman: "Jordan Lee",
        client: "Admiral Permian Resources LLC",
        prospect: "Glasscock Prospect",
        legal: "Sec 8 Blk B T2S R3E Abs 456",
        jobType: "Curative",
        days: 0.5,
        totalExpenses: 42.5,
      },
    ],
  },
  layout: {
    styles: {
      statusTable: {
        fontSize: 10,
        pdfFontSize: 8,
        headerBackgroundColor: "#eef4ff",
        headerBold: true,
        bodyWrap: true,
      },
    },
    sections: [
      {
        id: "detailsTable",
        type: "table",
        dataset: "landmanStatusDetails",
        title: "Landman Status",
        outputs: ["preview", "pdf", "excel"],
        style: "statusTable",
        summaryBadges: {
          dataset: "statusSummaryBadges",
        },
        columns: [
          { key: "workDate", label: "Date", format: "date", width: 8 },
          { key: "landman", label: "Landman", width: 14, wrap: true },
          { key: "client", label: "Client", width: 16, wrap: true },
          { key: "prospect", label: "Prospect", width: 16, wrap: true },
          { key: "legal", label: "Legal", width: 20, wrap: true },
          { key: "jobType", label: "Job Type", width: 10, wrap: true },
          { key: "days", label: "Days", format: "number", width: 6, horizontalAlign: "right" },
          { key: "totalExpenses", label: "Expenses", format: "money", width: 10, horizontalAlign: "right" },
        ],
      },
    ],
  },
};

const sampleLogoDataUrl = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAKAAAABQCAYAAACeXX40AAABGklEQVR42u3dMQ2AMBRF0WoBB2xYQARWUIcqBggWSNr+fs5N3g7kTO1AuaWOFZ9AAApACUABKAEoACUABaAEoACUQgBcjyv1BCCAAAIoAAEEEMC3edms0gAEEEAAAQQQQAABBBBAAHMAHBU2gAACCCCAAAIIIIAAAggggAACaAACCCCABiCAAAJoAAIIIIAGIIAAAmgAAggggAYggAACaAACCCCABiCAAAJoAAIIIIAWFGDtAAQQQAPwy3NP526VBiCAAAIIIIAAAggggAACCCCAAAIIIIAOoh1Eh72zdRcMIIAGYAuA2d+55wAEEEAAAQQQQAD9sPp/AQgggAACCCCAYwCUABSAEoACUAJQAEoACkAJQAEoAajwPZ6slKpxIs9bAAAAAElFTkSuQmCC";

const nativeInvoiceRows = Array.from({ length: 78 }, (_, index) => {
  const row = index + 1;
  const quantity = (row % 5) + 1;
  const rate = [125, 150, 175, 200][row % 4];
  return {
    workDate: `2026-10-${String((row % 28) + 1).padStart(2, "0")}`,
    landman: row % 3 === 0 ? "Taylor Sample" : row % 3 === 1 ? "Jordan Example" : "Morgan Fixture",
    description: row % 9 === 0
      ? "Extended title and curative review for a long legal description with enough text to verify wrapping behavior across preview and PDF output."
      : `Sample invoice line ${row}`,
    quantity,
    rate,
    amount: quantity * rate,
  };
});

const nativeInvoicePayload = {
  definitionId: "native-professional-invoice-v31",
  schemaVersion: "reports-v3.1",
  metadata: {
    reportName: "Native Professional Invoice Sample",
    sampleDataNotice: "Uses generated design-time sample data.",
  },
  assets: {
    companyLogo: {
      available: true,
      contentType: "image/png",
      url: sampleLogoDataUrl,
    },
  },
  data: {
    datasets: [
      {
        id: "invoiceHeader",
        label: "Invoice Header",
        kind: "single",
        fields: [
          { path: "companyName", label: "Company Name", type: "string" },
          { path: "companyAddressLine1", label: "Company Address 1", type: "string" },
          { path: "companyAddressLine2", label: "Company Address 2", type: "string" },
          { path: "companyContact", label: "Company Contact", type: "string" },
          { path: "clientName", label: "Bill To Name", type: "string" },
          { path: "clientAddressLine1", label: "Bill To Address 1", type: "string" },
          { path: "clientAddressLine2", label: "Bill To Address 2", type: "string" },
          { path: "attentionName", label: "Attention", type: "string" },
          { path: "invoiceNumber", label: "Invoice Number", type: "string" },
          { path: "invoiceDate", label: "Invoice Date", type: "date" },
          { path: "dueTerms", label: "Payment Terms", type: "string" },
          { path: "periodCovered", label: "Billing Period", type: "string" },
        ],
      },
      {
        id: "invoiceLines",
        label: "Invoice Line Items",
        kind: "collection",
        fields: [
          { path: "workDate", label: "Work Date", type: "date" },
          { path: "landman", label: "Landman", type: "string" },
          { path: "description", label: "Description", type: "string" },
          { path: "billingCategory", label: "Billing Category", type: "string" },
          { path: "quantity", label: "Qty", type: "number" },
          { path: "rate", label: "Rate", type: "money" },
          { path: "amount", label: "Amount", type: "money" },
        ],
      },
    ],
  },
  datasets: {
    invoiceHeader: {
      companyName: "Land Agility Sample Services",
      companyAddressLine1: "100 Sample Field Road",
      companyAddressLine2: "Midland, TX 79701",
      companyContact: "billing@example.test · (555) 010-0100",
      clientName: "Sample Exploration Company",
      clientAddressLine1: "200 Demo Client Avenue",
      clientAddressLine2: "Houston, TX 77002",
      attentionName: "Avery Approver",
      invoiceNumber: "INV-SAMPLE-5C",
      invoiceDate: "2026-10-17",
      dueTerms: "Net 30",
      periodCovered: "10/01/2026 - 10/31/2026",
    },
    invoiceLines: nativeInvoiceRows,
    emptyInvoiceLines: [],
  },
  layout: {
    version: "reports-v3.1",
    page: { size: "Letter", orientation: "portrait", margins: { top: 36, right: 36, bottom: 36, left: 36 } },
    outputs: { pdf: { renderer: "document" } },
    bands: [
      {
        id: "pageHeader",
        role: "pageHeader",
        height: 24,
        elements: [
          { id: "pageHeaderText", type: "text", x: 0, y: 0, width: 260, height: 14, text: "Sample data invoice preview", style: { fontSize: 9, color: "#63758f" } },
        ],
      },
      {
        id: "reportHeader",
        role: "reportHeader",
        height: 178,
        elements: [
          {
            id: "invoiceHeaderContainer",
            type: "container",
            x: 0,
            y: 0,
            width: 540,
            height: 156,
            padding: 10,
            gap: 12,
            layout: { mode: "columns", gap: 12, columns: [{ width: 74 }, { width: "1.25fr" }, { width: "1.2fr" }, { width: "1.15fr" }] },
            style: { backgroundColor: "#ffffff" },
            elements: [
              { id: "companyLogo", type: "image", x: 0, y: 0, width: 74, height: 64, asset: "companyLogo", fit: "contain", lockAspectRatio: true, style: { backgroundColor: "#ffffff" } },
              { id: "companyColumn", type: "container", height: 112, layout: { mode: "vertical", gap: 3 }, elements: [
                { id: "companyLabel", type: "text", width: 130, height: 12, text: "Company", style: { fontSize: 8, bold: true, color: "#63758f" } },
                { id: "companyName", type: "field", width: 150, height: 28, binding: "invoiceHeader.companyName", style: { fontSize: 11, bold: true, lineHeight: 1.15 } },
                { id: "companyAddress1", type: "field", width: 150, height: 14, binding: "invoiceHeader.companyAddressLine1", style: { fontSize: 9 } },
                { id: "companyAddress2", type: "field", width: 150, height: 14, binding: "invoiceHeader.companyAddressLine2", style: { fontSize: 9 } },
                { id: "companyContact", type: "field", width: 150, height: 22, binding: "invoiceHeader.companyContact", style: { fontSize: 8, lineHeight: 1.15, color: "#63758f" } },
              ] },
              { id: "billToColumn", type: "container", height: 118, layout: { mode: "vertical", gap: 3 }, elements: [
                { id: "billToLabel", type: "text", width: 130, height: 12, text: "Bill To", style: { fontSize: 8, bold: true, color: "#63758f" } },
                { id: "billToName", type: "field", width: 130, height: 26, binding: "invoiceHeader.clientName", style: { fontSize: 10, bold: true, lineHeight: 1.15 } },
                { id: "billToAddress1", type: "field", width: 130, height: 14, binding: "invoiceHeader.clientAddressLine1", style: { fontSize: 9 } },
                { id: "billToAddress2", type: "field", width: 130, height: 14, binding: "invoiceHeader.clientAddressLine2", style: { fontSize: 9 } },
                { id: "attentionLabel", type: "text", width: 130, height: 12, text: "Attention", style: { fontSize: 8, bold: true, color: "#63758f" } },
                { id: "attentionName", type: "field", width: 130, height: 14, binding: "invoiceHeader.attentionName", style: { fontSize: 9 } },
              ] },
              { id: "invoiceMetaColumn", type: "container", height: 132, layout: { mode: "vertical", gap: 4 }, elements: [
                { id: "invoiceTitle", type: "text", width: 120, height: 22, text: "INVOICE", style: { fontSize: 18, bold: true, horizontalAlign: "right" } },
                { id: "invoiceNumber", type: "field", width: 120, height: 16, binding: "invoiceHeader.invoiceNumber", style: { fontSize: 10, bold: true, horizontalAlign: "right" } },
                { id: "invoiceDate", type: "field", width: 120, height: 14, binding: "invoiceHeader.invoiceDate", format: "date", style: { fontSize: 9, horizontalAlign: "right" } },
                { id: "billingPeriod", type: "field", width: 120, height: 14, binding: "invoiceHeader.periodCovered", style: { fontSize: 8, horizontalAlign: "right" } },
                { id: "terms", type: "field", width: 120, height: 14, binding: "invoiceHeader.dueTerms", style: { fontSize: 9, horizontalAlign: "right", color: "#ef4444" } },
              ] },
            ],
          },
        ],
      },
      {
        id: "detail",
        role: "detail",
        sizing: "flow",
        height: 84,
        elements: [
          {
            id: "invoiceLinesTable",
            type: "table",
            x: 0,
            y: 0,
            width: 540,
            height: 58,
            dataset: "invoiceLines",
            emptyText: "No invoice line sample records are loaded.",
            style: { fontSize: 8.5, lineHeight: 1.25, borderColor: "#808080", headerBackgroundColor: "#f0f5ff", footerBackgroundColor: "#f8fbff" },
            header: { repeat: true, style: { bold: true } },
            row: { padding: { top: 4, right: 4, bottom: 4, left: 4 } },
            columns: [
              { id: "workDate", label: "Date", binding: "workDate", width: 62, format: "date" },
              { id: "landman", label: "Landman", binding: "landman", width: 86 },
              { id: "description", label: "Description", binding: "description", width: 214 },
              { id: "quantity", label: "Qty", binding: "quantity", width: 48, format: "number", horizontalAlign: "right" },
              { id: "rate", label: "Rate", binding: "rate", width: 58, format: "money", horizontalAlign: "right" },
              { id: "amount", label: "Amount", binding: "amount", width: 72, format: "money", horizontalAlign: "right" },
            ],
            footer: { enabled: true, cells: [
              { columnId: "description", value: "Total", style: { bold: true, horizontalAlign: "right" } },
              { columnId: "amount", aggregate: "sum", format: "money", style: { bold: true, horizontalAlign: "right" } },
            ] },
            pagination: { repeatHeader: true },
          },
        ],
      },
      {
        id: "pageFooter",
        role: "pageFooter",
        height: 24,
        elements: [
          { id: "sampleFooter", type: "text", x: 0, y: 6, width: 360, height: 12, text: "Generated sample data for designer verification only.", style: { fontSize: 8, color: "#63758f" } },
        ],
      },
    ],
  },
} as unknown as FormattedInvoicePayload;

const nativeGroupedInvoicePayload = (() => {
  const payload = JSON.parse(JSON.stringify(nativeInvoicePayload)) as any;
  payload.definitionId = "native-grouped-invoice-v31";
  payload.metadata.reportName = "Native Grouped Invoice Sample";
  payload.metadata.sampleDataNotice = "Uses deterministic grouped design-time sample data.";
  payload.datasets.invoiceLines = Array.from({ length: 96 }, (_, index) => {
    const row = index + 1;
    const landman = row <= 38 ? "Avery Landman" : row <= 66 ? "Blake Broker" : row <= 82 ? "Casey Curative" : "";
    const billingCategory = row % 4 === 0 ? "Curative" : row % 3 === 0 ? "Leasing" : "Title";
    const quantity = (row % 4) + 1;
    const rate = landman ? [125, 150, 175][row % 3] : 100;
    return {
      workDate: `2026-10-${String((row % 28) + 1).padStart(2, "0")}`,
      landman,
      billingCategory,
      project: row % 2 === 0 ? "Delaware Basin" : "Midland Basin",
      description: row % 11 === 0
        ? "Long grouped activity narrative that intentionally wraps so grouped pagination can be verified across Preview and PDF output."
        : `Grouped sample invoice line ${row}`,
      quantity,
      rate,
      amount: quantity * rate,
    };
  });
  const detailBand = payload.layout.bands.find((band: any) => band.id === "detail");
  const table = detailBand.elements.find((element: any) => element.id === "invoiceLinesTable");
  table.columns.splice(3, 0, { id: "billingCategory", label: "Category", binding: "billingCategory", width: 72 });
  table.columns.find((column: any) => column.id === "description").width = 142;
  table.groups = [{
    id: "group-by-landman",
    field: "landman",
    sort: "asc",
    header: { visible: true, height: 22, label: "Landman", valueFormat: "text", style: { backgroundColor: "#f8fbff", bold: true } },
    footer: {
      visible: true,
      height: 24,
      cells: [
        { columnId: "workDate", colSpan: 6, value: "Landman subtotal", style: { bold: true, horizontalAlign: "right", backgroundColor: "#f8fbff" } },
        { columnId: "amount", aggregate: { fn: "sum", field: "amount", scope: "group" }, format: "money", style: { bold: true, horizontalAlign: "right" } },
      ],
    },
    pagination: { keepHeaderWithFirstRow: true, keepFooterWithLastRow: true, repeatHeaderOnContinuation: true, spacingAfter: 12 },
  }];
  table.footer.cells = [
    { columnId: "workDate", colSpan: 6, value: "Line subtotal", style: { bold: true, horizontalAlign: "right" } },
    { columnId: "amount", aggregate: { fn: "sum", field: "amount", scope: "report" }, format: "money", style: { bold: true, horizontalAlign: "right" } },
  ];
  return payload as FormattedInvoicePayload;
})();

const nativeGroupedDraftInvoicePayload = (() => {
  const payload: FormattedInvoicePayload & JsonRecord = clonePayload(nativeGroupedInvoicePayload);
  payload.definitionId = "native-grouped-draft-invoice-v31";
  payload.definitionVersion = 1;
  payload.reportKey = "native-grouped-draft-invoice";
  payload.reportType = "invoice";
  payload.requiredFilters = { client: true, dateRange: true };
  payload.capabilities = { preview: true, pdf: true, excel: false };
  payload.assets = {};
  payload.metadata = { ...payload.metadata, reportName: "Native Grouped Draft Invoice", sampleDataNotice: "Editor-only sample records. This template is for unbilled previews, not generated invoices." };
  const sampleLines = asArray(payload.datasets.invoiceLines).map(asRecord);
  payload.datasets.invoiceTotals = {
    days: sampleLines.reduce((sum, line) => sum + Number(line.quantity), 0),
    professionalServices: sampleLines.reduce((sum, line) => sum + Number(line.quantity) * Number(line.rate), 0),
    expenses: 0,
    amountDue: sampleLines.reduce((sum, line) => sum + Number(line.amount), 0),
  };
  const data = asRecord(payload.data);
  data.datasets = asArray(data.datasets).map((value) => {
    const dataset = asRecord(value);
    return { ...dataset, ...(dataset.id === "invoiceHeader" ? { source: "invoice" } : dataset.id === "invoiceLines" ? { source: "invoiceTimeEntries" } : {}) };
  });
  asArray(data.datasets).push({
    id: "invoiceTotals", label: "Invoice Totals", kind: "single", source: "invoice",
    fields: ["days", "professionalServices", "expenses", "amountDue"].map((path) => ({ path, type: "number" })),
  });
  payload.data = data;
  const layout = asRecord(payload.layout);
  const bands = asArray(layout.bands).map(asRecord);
  const header = bands.find((band) => band.id === "reportHeader")!;
  header.height = 136;
  const field = (id: string, binding: string, height = 14, fontSize = 9): JsonRecord =>
    ({ id, type: "field", width: 150, height, binding, style: { fontSize, color: "#23324a" } });
  const location = (id: string, binding: string): JsonRecord => ({
    id, type: "container", width: 150, height: 14, layout: { mode: "absolute" },
    elements: [
      { id: `${id}Label`, type: "text", x: 0, y: 0, width: 42, height: 14, text: "Location:", style: { fontSize: 8, color: "#63758f" } },
      { ...field(`${id}Value`, binding, 14, 8), x: 44, y: 0, width: 106 },
    ],
  });
  const label = (id: string, text: string): JsonRecord =>
    ({ id, type: "text", width: 150, height: 10, text, style: { fontSize: 8, color: "#63758f", bold: true } });
  header.elements = [{
    id: "invoiceHeaderContainer", type: "container", x: 0, y: 0, width: 540, height: 124, padding: 10,
    layout: { mode: "columns", gap: 12, columns: [{ width: 76 }, { width: 150 }, { width: 150 }, { width: 108 }] },
    elements: [
      { id: "companyLogo", type: "image", x: 0, y: 0, width: 76, height: 64, asset: "companyLogo", fit: "contain", lockAspectRatio: true },
      { id: "companyColumn", type: "container", height: 104, layout: { mode: "vertical", gap: 3 }, elements: [
        label("companyLabel", "FROM"),
        { ...field("companyName", "invoiceHeader.companyName", 32, 11), style: { fontSize: 11, bold: true, color: "#23324a" } },
        field("companyAddress1", "invoiceHeader.companyAddressLine1", 12, 8),
        location("companyLocation", "invoiceHeader.companyAddressLine2"),
        field("companyContact", "invoiceHeader.companyContact", 22, 8),
      ] },
      { id: "billToColumn", type: "container", height: 104, layout: { mode: "vertical", gap: 3 }, elements: [
        label("billToLabel", "BILL TO"),
        { ...field("billToName", "invoiceHeader.clientName", 28, 10), style: { fontSize: 10, bold: true, color: "#23324a" } },
        field("billToAddress1", "invoiceHeader.clientAddressLine1", 12, 8),
        location("clientLocation", "invoiceHeader.clientAddressLine2"),
        field("attentionName", "invoiceHeader.attentionName", 14, 8),
      ] },
      { id: "invoiceMetaColumn", type: "container", height: 104, layout: { mode: "vertical", gap: 3 }, elements: [
        { id: "invoiceTitle", type: "text", width: 108, height: 22, text: "DRAFT", style: { fontSize: 18, bold: true, horizontalAlign: "right", color: "#23324a" } },
        { id: "draftNotice", type: "text", width: 108, height: 10, text: "UNBILLED PREVIEW", style: { fontSize: 8, horizontalAlign: "right", color: "#63758f" } },
        { id: "invoiceDateRow", type: "container", width: 108, height: 14, layout: { mode: "absolute" }, elements: [
          { id: "invoiceDateLabel", type: "text", x: 0, y: 0, width: 30, height: 14, text: "As of", style: { fontSize: 8, color: "#63758f" } },
          { ...field("invoiceDate", "invoiceHeader.invoiceDate", 14, 8), x: 32, y: 0, width: 76, format: "date", style: { fontSize: 8, horizontalAlign: "right" } },
        ] },
        { ...field("billingPeriod", "invoiceHeader.periodCovered", 22, 8), width: 108, style: { fontSize: 8, horizontalAlign: "right" } },
        { ...field("terms", "invoiceHeader.dueTerms", 24, 8), width: 108, style: { fontSize: 8, horizontalAlign: "right", color: "#63758f" } },
      ] },
    ],
  }];
  const pageHeader = bands.find((band) => band.id === "pageHeader")!;
  pageHeader.height = 16;
  asArray(pageHeader.elements).map(asRecord)[0].text = "DRAFT INVOICE / UNBILLED TIME";
  asArray(asRecord(bands.find((band) => band.id === "pageFooter")).elements).map(asRecord)[0].text = "Unbilled preview. Not a generated invoice.";
  const table = asArray(asRecord(bands.find((band) => band.id === "detail")).elements).map(asRecord).find((element) => element.id === "invoiceLinesTable")!;
  const widths: Record<string, number> = { workDate: 62, description: 230, quantity: 36, rate: 60, amount: 76 };
  const columns: JsonRecord[] = asArray(table.columns).map(asRecord).map((column) => ({
    ...column, ...(widths[String(column.id)] ? { width: widths[String(column.id)] } : {}),
    ...(["landman", "billingCategory"].includes(String(column.id)) ? { visible: false } : {}),
  }));
  columns.splice(columns.findIndex((column) => column.id === "amount"), 0, {
    id: "labor", label: "Labor Only", width: 76, format: "money", horizontalAlign: "right",
    expr: { op: "mul", left: { field: "quantity" }, right: { field: "rate" } },
  });
  table.columns = columns;
  table.sort = [{ field: "workDate", direction: "asc", type: "date", nulls: "last" }];
  table.emptyText = "No unbilled invoice lines for the selected period.";
  table.style = { ...asRecord(table.style), color: "#23324a", borderColor: "#d8e1ec" };
  const cells = (scope: "group" | "report", text: string, backgroundColor: string) => [
    { columnId: "workDate", colSpan: 6, value: text, horizontalAlign: "left", style: { bold: true, backgroundColor } },
    { columnId: "labor", aggregate: { fn: "sum", field: "labor", scope }, format: "money", horizontalAlign: "right", style: { bold: true, backgroundColor } },
    { columnId: "amount", aggregate: { fn: "sum", field: "amount", scope }, format: "money", horizontalAlign: "right", style: { bold: true, backgroundColor } },
  ];
  const group = asArray(table.groups).map(asRecord)[0];
  group.footer = { ...asRecord(group.footer), cells: cells("group", "Landman subtotal", "#f8fbff") };
  group.pagination = { ...asRecord(group.pagination), spacingAfter: 8 };
  table.footer = { enabled: true, minHeight: 24, cells: cells("report", "Line subtotal (including expenses)", "#eef4ff") };
  bands.push({
    id: "fullInvoiceTotal", role: "reportFooter", height: 42,
    elements: [
      { id: "amountDueLabel", type: "text", x: 0, y: 8, width: 400, height: 16, text: "Draft invoice amount due", style: { fontSize: 11, bold: true, color: "#23324a" } },
      { id: "amountDueNote", type: "text", x: 0, y: 26, width: 400, height: 12, text: "Includes invoice-level adjustments", style: { fontSize: 8, color: "#63758f" } },
      { id: "fullAmountDue", type: "field", x: 420, y: 8, width: 120, height: 20, binding: "invoiceTotals.amountDue", format: "money", style: { fontSize: 12, bold: true, horizontalAlign: "right", color: "#23324a" } },
    ],
  });
  layout.bands = bands;
  return payload;
})();

export const DESIGNER_SAMPLES: DesignerSample[] = [
  {
    id: "formatted-invoice-header",
    name: "Formatted Invoice Report",
    description: "Full report definition with a key-value header, explicit columns, style spacing, and a table section.",
    payload: formattedInvoicePayload,
  },
  {
    id: "landman-status-table",
    name: "Landman Status Report",
    description: "Full report definition with a dense table, summary badges, wrapping, formats, and widths.",
    payload: landmanStatusPayload,
  },
  {
    id: "native-professional-invoice-v31",
    name: "Native Professional Invoice (v3.1)",
    description: "Native v3.1 invoice fixture with logo, editable header, repeating table, totals, and multi-page generated sample rows.",
    payload: nativeInvoicePayload,
  },
  {
    id: "native-grouped-invoice-v31",
    name: "Native Grouped Invoice (v3.1 6A)",
    description: "Native v3.1 grouped invoice fixture with landman group headers, scoped subtotals, line subtotal, and multi-page continuation.",
    payload: nativeGroupedInvoicePayload,
  },
  {
    id: "native-grouped-draft-invoice-v31",
    name: "Native Grouped Draft Invoice (v3.1)",
    description: "Draft-only unbilled invoice with chronological grouped lines, labor and authoritative amount subtotals, and a separately bound full amount due. Use the general invoice template for generated invoices.",
    payload: nativeGroupedDraftInvoicePayload,
  },
];

export function clonePayload(payload: FormattedInvoicePayload): FormattedInvoicePayload {
  return JSON.parse(JSON.stringify(payload)) as FormattedInvoicePayload;
}
